/**
 * Wrapper component to handle address form input fields
 * that use Formik and Reactstrap.
 */
import { Loader } from "@googlemaps/js-api-loader";
import { useField } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { FormFeedback, FormGroup, Input, Label } from "reactstrap";

const { REACT_APP_GOOGLE_API_KEY } = process.env;

const loader = new Loader({
  apiKey: REACT_APP_GOOGLE_API_KEY,
  libraries: ["places"],
});

/**
 * Formik Address Input field.
 */
const AddressInput = ({ label, floating, bsSize, ...other }) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const firstOnBlur = useRef(null);

  const [googleLoaded, setGoogleLoaded] = useState(!!window.google);
  const [field, meta, helpers] = useField(other);
  const { onBlur, ...fieldProps } = field;
  const invalid = Boolean(meta.touched && meta.error);

  const handleOnBlur = useCallback(
    (e) => {
      if (!firstOnBlur.current) {
        onBlur(e);
      } else {
        firstOnBlur.current = false;
      }
    },
    [onBlur]
  );

  useEffect(() => {
    if (!googleLoaded) {
      loader
        .importLibrary("places")
        .then(() => {
          setGoogleLoaded(true);
        })
        .catch((e) => {
          console.log(e);
        });
    }
  }, [googleLoaded]);

  useEffect(() => {
    if (
      googleLoaded &&
      window.google &&
      inputRef.current &&
      !autocompleteRef.current
    ) {
      firstOnBlur.current = true;

      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          componentRestrictions: { country: ["us"] },
          fields: ["formatted_address"],
          types: ["address"],
        }
      );

      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current.getPlace();
        helpers.setValue(place.formatted_address);
      });
    }
  }, [googleLoaded, helpers]);

  return (
    <FormGroup floating={floating}>
      {!floating && label && (
        <Label for={other.id} size={bsSize}>
          {label}
        </Label>
      )}
      <Input
        {...fieldProps}
        {...other}
        bsSize={bsSize}
        value={fieldProps.value || ""}
        onBlur={handleOnBlur}
        innerRef={inputRef}
        invalid={invalid}
      />
      {floating && label && <Label for={other.id}>{label}</Label>}
      {invalid ? <FormFeedback>{meta.error}</FormFeedback> : null}
    </FormGroup>
  );
};

export default AddressInput;
